import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, GitCompareArrows, Loader2 } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";

const COLORS = ["#4338CA", "#059669", "#D97706", "#DB2777", "#0891B2"];

const Bar = ({ v, color }) => (
  <div className="flex items-center gap-2">
    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v ?? 0}%`, background: color }} /></div>
    <span className="font-heading font-bold text-sm text-slate-800 w-10 text-right">{v ?? "—"}%</span>
  </div>
);

const Row = ({ label, children, cols }) => (
  <div className="grid gap-4 py-3 border-b border-slate-100 last:border-0" style={{ gridTemplateColumns: `160px repeat(${cols}, minmax(0,1fr))` }}>
    <p className="font-body text-xs uppercase tracking-wide text-slate-400 pt-1">{label}</p>
    {children}
  </div>
);

const Compare = () => {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { if (!loading && !isAdminRole(user)) navigate("/"); }, [user, loading, navigate]);
  useEffect(() => {
    if (!ids) { navigate("/admin"); return; }
    api.get(`/admin/compare?ids=${encodeURIComponent(ids)}`).then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not load comparison"); navigate("/admin"); });
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>;

  const n = data.length;
  const radar = ["R", "I", "A", "S", "E", "C"].map((t) => {
    const row = { subject: data[0].scores.holland.scores.find((s) => s.type === t)?.label || t };
    data.forEach((d, i) => { row[`c${i}`] = d.scores.holland.scores.find((s) => s.type === t)?.percent ?? 0; });
    return row;
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="compare-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <Button variant="ghost" onClick={() => navigate("/admin")} className="font-heading font-bold text-slate-600 -ml-3 mb-2" data-testid="compare-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Reports
            </Button>
            <h1 className="font-heading font-extrabold text-3xl text-slate-900 flex items-center gap-2"><GitCompareArrows className="w-7 h-7 text-primary" /> Candidate comparison</h1>
            <p className="font-body text-slate-500 mt-1">Side-by-side scores and career matches for {n} candidates.</p>
          </div>
        </div>

        <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))` }}>
          {data.map((d, i) => (
            <div key={d.assessment_id} className="bg-white rounded-2xl border border-slate-200 p-5 border-t-4" style={{ borderTopColor: COLORS[i] }} data-testid={`compare-card-${d.code}`}>
              <p className="font-heading font-extrabold text-lg text-slate-900">{d.candidate_name}</p>
              <p className="font-body text-xs text-slate-500 mt-0.5"><span className="font-mono">{d.code}</span> · {d.completed_at ? new Date(d.completed_at).toLocaleDateString() : "—"} · {d.goal}</p>
              {d.headline && <p className="font-body text-sm text-slate-600 mt-3 italic line-clamp-3">"{d.headline}"</p>}
              <Button size="sm" variant="outline" className="rounded-lg mt-4 font-heading font-bold" onClick={() => navigate(`/admin/report/${d.assessment_id}`)} data-testid={`compare-open-${d.code}`}>
                <FileText className="w-3.5 h-3.5 mr-1" /> Full report
              </Button>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Scores</h2>
          <Row label="Cognitive ability" cols={n}>{data.map((d, i) => <Bar key={i} v={d.scores.cognitive?.percent} color={COLORS[i]} />)}</Row>
          <Row label="Aptitude" cols={n}>{data.map((d, i) => <Bar key={i} v={d.scores.aptitude?.percent} color={COLORS[i]} />)}</Row>
          <Row label="DISC profile" cols={n}>
            {data.map((d, i) => (
              <div key={i}>
                <p className="font-heading font-bold text-slate-900">{d.scores.disc?.primary_label} <span className="text-slate-400 font-medium">/ {d.scores.disc?.secondary_label}</span></p>
                <div className="flex gap-1 mt-2">
                  {(d.scores.disc?.scores || []).map((s) => (
                    <div key={s.trait} className="flex-1 text-center">
                      <div className="h-10 rounded bg-slate-100 relative overflow-hidden"><div className="absolute bottom-0 w-full rounded" style={{ height: `${s.percent}%`, background: COLORS[i], opacity: 0.85 }} /></div>
                      <p className="font-body text-[10px] text-slate-500 mt-1">{s.trait} {s.percent}%</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Row>
          <Row label="Holland code" cols={n}>{data.map((d, i) => <p key={i} className="font-heading font-extrabold text-xl font-mono" style={{ color: COLORS[i] }}>{d.scores.holland?.code}</p>)}</Row>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-heading font-bold text-lg text-slate-900 mb-2">Interests (RIASEC)</h2>
            <div className="w-full h-80" data-testid="compare-radar">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radar} outerRadius="72%">
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: "#475569", fontSize: 11, fontFamily: "Figtree" }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  {data.map((d, i) => <Radar key={i} name={d.candidate_name} dataKey={`c${i}`} stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.18} strokeWidth={2} />)}
                  <Legend wrapperStyle={{ fontFamily: "Figtree", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Top career matches</h2>
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))` }}>
              {data.map((d, i) => (
                <ol key={i} className="space-y-2" data-testid={`compare-matches-${d.code}`}>
                  {(d.career_matches || []).map((m, j) => (
                    <li key={j} className="rounded-xl border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-heading font-bold text-sm text-slate-900">{j + 1}. {m.title}</p>
                        <span className="font-heading font-extrabold text-sm shrink-0" style={{ color: COLORS[i] }}>{m.fit}%</span>
                      </div>
                      <div className="h-1 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${m.fit}%`, background: COLORS[i] }} /></div>
                    </li>
                  ))}
                  {(d.career_matches || []).length === 0 && <p className="font-body text-xs text-slate-400">No matches generated.</p>}
                </ol>
              ))}
            </div>
            {data.some((d) => d.strengths?.length) && (
              <>
                <h2 className="font-heading font-bold text-lg text-slate-900 mt-8 mb-3">Key strengths</h2>
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))` }}>
                  {data.map((d, i) => (
                    <ul key={i} className="space-y-1.5">
                      {(d.strengths || []).map((s, j) => <li key={j} className="font-body text-sm text-slate-600 flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: COLORS[i] }} />{s}</li>)}
                    </ul>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Compare;
